import { Box, Button, Stack, Table, TableBody, TableCell, TableRow } from "@mui/material";
import { useEffect, useState } from "react";
import { type TokenResponse, decodeToken } from "../../data/api";
import { StatusBadge } from "../../ui/components/StatusBadge";
import { Hint, Mono, Tag, Tags } from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";

const CLAIM_PREFIX = "https://aiui.run/claims/";

/** Shows a minted token: its scopes, the AIUI claims, a countdown, and the raw JWT. */
export function TokenView({ token }: { token: TokenResponse }): React.JSX.Element {
  const tokens = useTokens();
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    // A live countdown makes the 15-minute ceiling visible rather than theoretical.
    const timer = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(timer);
  }, []);

  let decoded: { header: unknown; claims: Record<string, unknown> };
  try {
    decoded = decodeToken(token.access_token);
  } catch {
    return <StatusBadge label="Token is not a decodable JWT" tone="danger" />;
  }

  const claims = decoded.claims;
  const exp = typeof claims.exp === "number" ? claims.exp : 0;
  const remaining = Math.max(0, exp - now);
  const scopes = token.scope === "" ? [] : token.scope.split(" ").sort();

  const rows = [
    ...Object.entries(claims)
      .filter(([key]) => key.startsWith(CLAIM_PREFIX))
      .map(([key, value]) => [key.slice(CLAIM_PREFIX.length), value] as const),
    ...(["iss", "sub", "aud", "client_id", "jti", "sid"] as const)
      .map((key) => [key, claims[key]] as const)
      .filter(([, value]) => value !== undefined),
  ];

  const label = { typography: "overline", color: tokens.ink.muted, mb: 2 } as const;

  return (
    <Stack spacing={6}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <StatusBadge
          label={
            remaining > 0
              ? `Valid ${Math.floor(remaining / 60)}m ${String(remaining % 60).padStart(2, "0")}s`
              : "Expired"
          }
          tone={remaining > 60 ? "success" : "danger"}
        />
        <Box sx={{ typography: "body2", color: tokens.ink.muted }}>
          {token.token_type} · {new TextEncoder().encode(token.access_token).length} bytes
        </Box>
      </Stack>

      <div>
        <Box sx={label}>Scopes ({scopes.length})</Box>
        {scopes.length === 0 ? (
          <Hint sx={{ mt: 0 }}>None.</Hint>
        ) : (
          <Tags>
            {scopes.map((scope) => (
              <Tag key={scope} label={scope} />
            ))}
          </Tags>
        )}
      </div>

      <div>
        <Box sx={label}>Claims</Box>
        <Table size="small">
          <TableBody>
            {rows.map(([key, value]) => (
              <TableRow key={key}>
                <TableCell sx={{ width: 170, pl: 0, color: tokens.ink.muted }}>{key}</TableCell>
                <TableCell sx={{ pr: 0 }}>
                  <Mono sx={{ wordBreak: "break-all" }}>
                    {Array.isArray(value) ? value.join(", ") : String(value)}
                  </Mono>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ ...label, mb: 0 }}>{showRaw ? "Raw JWT" : "Header"}</Box>
          <Button size="small" variant="text" onClick={() => setShowRaw(!showRaw)}>
            {showRaw ? "Show header" : "Show raw"}
          </Button>
        </Stack>
        <Mono
          sx={{
            display: "block",
            p: 3,
            bgcolor: tokens.surface.cardSunken,
            border: `1px solid ${tokens.line.faint}`,
            borderRadius: `${tokens.radius.md}px`,
            fontSize: 11.5,
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {showRaw
            ? token.access_token.replace(/\./g, ".\n")
            : JSON.stringify(decoded.header, null, 2)}
        </Mono>
      </div>
    </Stack>
  );
}
